import { useState, useEffect, useCallback } from "react";
import { useGamepad } from "../../hooks/useGamepad";
import styles from "./VirtualKeyboardModal.module.css";

const KEYBOARD_LAYOUT = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
  ["SPACE"]
];

export const VirtualKeyboardModal = ({
  initialValue = "",
  onSubmit,
  onClose,
  theme = 0,
  game,
  maxLength = 20,
  allowedPattern = null // e.g., /[A-Z0-9]/ for room codes
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [selectedRow, setSelectedRow] = useState(0);
  const [selectedCol, setSelectedCol] = useState(0);
  const [gamepadEnabled, setGamepadEnabled] = useState(false);

  // Get the currently selected key
  const getCurrentKey = useCallback(() => {
    if (selectedRow < KEYBOARD_LAYOUT.length && selectedCol < KEYBOARD_LAYOUT[selectedRow].length) {
      return KEYBOARD_LAYOUT[selectedRow][selectedCol];
    }
    return null;
  }, [selectedRow, selectedCol]);

  // Handle key selection
  const handleKeySelect = useCallback((key) => {
    if (!key) return;

    game?.sounds?.clickSound?.play();

    if (key === "DONE") {
      onSubmit(inputValue);
      onClose();
    } else if (key === "BACKSPACE") {
      setInputValue(prev => prev.slice(0, -1));
    } else if (key === "SPACE") {
      if (inputValue.length < maxLength) {
        setInputValue(prev => prev + " ");
      }
    } else {
      // Check if character is allowed
      if (allowedPattern && !allowedPattern.test(key)) {
        game?.sounds?.rotateBlockSound?.play(); // Error sound
        return;
      }
      if (inputValue.length < maxLength) {
        setInputValue(prev => prev + key);
      }
    }
  }, [inputValue, maxLength, allowedPattern, onSubmit, onClose, game]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    game?.sounds?.fall?.play();

    if (direction === 'left') {
      setSelectedCol((prev) => {
        const maxCol = KEYBOARD_LAYOUT[selectedRow].length - 1;
        return prev > 0 ? prev - 1 : maxCol;
      });
    } else if (direction === 'right') {
      setSelectedCol((prev) => {
        const maxCol = KEYBOARD_LAYOUT[selectedRow].length - 1;
        return prev < maxCol ? prev + 1 : 0;
      });
    } else if (direction === 'up') {
      setSelectedRow((prev) => {
        const newRow = prev > 0 ? prev - 1 : KEYBOARD_LAYOUT.length - 1;
        setSelectedCol((col) => Math.min(col, KEYBOARD_LAYOUT[newRow].length - 1));
        return newRow;
      });
    } else if (direction === 'down') {
      setSelectedRow((prev) => {
        const newRow = prev < KEYBOARD_LAYOUT.length - 1 ? prev + 1 : 0;
        setSelectedCol((col) => Math.min(col, KEYBOARD_LAYOUT[newRow].length - 1));
        return newRow;
      });
    }
  }, [selectedRow, game]);

  // Handle gamepad button presses
  const handleGamepadButton = useCallback((button) => {
    if (button === 'X') {
      const key = getCurrentKey();
      handleKeySelect(key);
    } else if (button === 'B') {
      setInputValue(prev => prev.slice(0, -1));
      game?.sounds?.clickSound?.play();
    } else if (button === 'Y') {
      onSubmit(inputValue);
      onClose();
      game?.sounds?.clickSound?.play();
    } else if (button === 'A') {
      onClose();
      game?.sounds?.clickSound?.play();
    }
  }, [getCurrentKey, handleKeySelect, inputValue, onSubmit, onClose, game]);

  // Delay gamepad input to prevent input bleed-through from the button that opened the modal
  useEffect(() => {
    const timer = setTimeout(() => {
      setGamepadEnabled(true);
    }, 300); // 300ms delay for user to release the button

    return () => clearTimeout(timer);
  }, []);

  // Use the reusable gamepad hook
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButton,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: gamepadEnabled
  });

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();

      if (key === "arrowleft") {
        e.preventDefault();
        setSelectedCol((prev) => {
          const maxCol = KEYBOARD_LAYOUT[selectedRow].length - 1;
          return prev > 0 ? prev - 1 : maxCol;
        });
        game?.sounds?.fall?.play();
      } else if (key === "arrowright") {
        e.preventDefault();
        setSelectedCol((prev) => {
          const maxCol = KEYBOARD_LAYOUT[selectedRow].length - 1;
          return prev < maxCol ? prev + 1 : 0;
        });
        game?.sounds?.fall?.play();
      } else if (key === "arrowup") {
        e.preventDefault();
        setSelectedRow((prev) => {
          const newRow = prev > 0 ? prev - 1 : KEYBOARD_LAYOUT.length - 1;
          setSelectedCol((col) => Math.min(col, KEYBOARD_LAYOUT[newRow].length - 1));
          return newRow;
        });
        game?.sounds?.fall?.play();
      } else if (key === "arrowdown") {
        e.preventDefault();
        setSelectedRow((prev) => {
          const newRow = prev < KEYBOARD_LAYOUT.length - 1 ? prev + 1 : 0;
          setSelectedCol((col) => Math.min(col, KEYBOARD_LAYOUT[newRow].length - 1));
          return newRow;
        });
        game?.sounds?.fall?.play();
      } else if (key === "enter" || key === " ") {
        e.preventDefault();
        const selectedKey = getCurrentKey();
        handleKeySelect(selectedKey);
      } else if (key === "escape") {
        e.preventDefault();
        onClose();
      } else if (key === "backspace") {
        e.preventDefault();
        setInputValue(prev => prev.slice(0, -1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedRow, selectedCol, getCurrentKey, handleKeySelect, onClose, game]);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${styles[`modalContent-theme-${theme}`]}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Enter Text</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        {/* Live Preview */}
        <div className={`${styles.inputPreview} ${styles[`inputPreview-theme-${theme}`]}`}>
          {inputValue || <span className={styles.placeholder}>Start typing...</span>}
          <span className={styles.cursor}>|</span>
        </div>

        {/* Virtual Keyboard */}
        <div className={styles.keyboard}>
          {KEYBOARD_LAYOUT.map((row, rowIndex) => (
            <div key={rowIndex} className={styles.row}>
              {row.map((key, colIndex) => {
                const isSelected = selectedRow === rowIndex && selectedCol === colIndex;
                const isSpecialKey = key === "SPACE";

                return (
                  <button
                    key={`${rowIndex}-${colIndex}`}
                    className={`
                      ${styles.key}
                      ${styles[`key-theme-${theme}`]}
                      ${isSelected ? styles.selected : ""}
                      ${isSelected ? styles[`selected-theme-${theme}`] : ""}
                      ${isSpecialKey ? styles.specialKey : ""}
                    `}
                    onClick={() => handleKeySelect(key)}
                    type="button"
                  >
                    {key === "SPACE" ? "___" : key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Help Text */}
        <div className={styles.helpText}>
          <p>X: Select • B: Backspace • Y: Submit • A: Cancel</p>
        </div>
      </div>
    </div>
  );
};
